import {ImageWidget} from '@app/dashboard/biolink/biolink-editor/content/widgets/image-widget/image-widget-dialog';
import {WidgetRendererProps} from '@app/dashboard/biolink/biolink-editor/content/widgets/types/widget-renderer-props';
import clsx from 'clsx';
import {ImageIcon} from '@common/icons/material/Image';

export function ImageWidgetRenderer({
  widget,
  variant,
}: WidgetRendererProps<ImageWidget>) {
  const customWidth = widget.config.width;
  const customHeight = widget.config.height;
  const maskEnabled = widget.config.maskEnabled !== 'false'; // Default to true if undefined
  const cropX = widget.config.cropX;
  const cropY = widget.config.cropY;
  const cropScale = widget.config.cropScale;
  const cropRotation = widget.config.cropRotation;
  const maskSize = widget.config.maskSize;
  const maskWidth = widget.config.maskWidth;
  const maskHeight = widget.config.maskHeight;
  const maskX = widget.config.maskX;
  const maskY = widget.config.maskY;
  const maskShape = widget.config.maskShape;
  const maskBorderRadius = widget.config.maskBorderRadius;
  const fullImageWidth = widget.config.fullImageWidth;
  const fullImageHeight = widget.config.fullImageHeight;
  const fullImageMaxWidth = widget.config.fullImageMaxWidth;
  const filterBrightness = widget.config.filterBrightness;
  const filterContrast = widget.config.filterContrast;
  const filterSaturation = widget.config.filterSaturation;
  const filterBlur = widget.config.filterBlur;
  const filterSepia = widget.config.filterSepia;
  
  // Only apply custom dimensions if they are actually provided and not empty
  // AND only for biolink page variant (not for editor)
  const hasCustomDimensions = (variant !== 'editor') && 
                              ((customWidth?.trim() && customWidth.trim() !== '') || 
                               (customHeight?.trim() && customHeight.trim() !== ''));
  
  // Check if avatar has crop settings
  const hasCropSettings = widget.config.type === 'avatar' && 
                         cropX !== undefined && 
                         cropY !== undefined && 
                         cropScale !== undefined;

  // Check if avatar has mask settings
  const hasMaskSettings = widget.config.type === 'avatar' &&
                          maskSize !== undefined &&
                          maskX !== undefined &&
                          maskY !== undefined;
  
  // Check if avatar has filter settings
  const hasFilterSettings = widget.config.type === 'avatar' && 
                           (filterBrightness !== undefined || 
                            filterContrast !== undefined || 
                            filterSaturation !== undefined || 
                            filterBlur !== undefined || 
                            filterSepia !== undefined);
  
  const imageStyle = hasCustomDimensions ? {
    ...(customWidth?.trim() && customWidth.trim() !== '' && { width: customWidth.trim() }),
    ...(customHeight?.trim() && customHeight.trim() !== '' && { height: customHeight.trim() }),
    objectFit: 'cover' as const,
  } : (hasCropSettings || hasMaskSettings) && variant === 'biolinkPage' ? {
    objectPosition: `${cropX}% ${cropY}%`,
    transform: `scale(${cropScale}) rotate(${cropRotation || 0}deg)`,
    objectFit: 'cover' as const,
    width: hasMaskSettings ? `${maskSize}px` : undefined,
    height: hasMaskSettings ? `${maskSize}px` : undefined,
    filter: hasFilterSettings ? `
      brightness(${100 + (parseFloat(filterBrightness || '0'))}%)
      contrast(${100 + (parseFloat(filterContrast || '0'))}%)
      saturate(${100 + (parseFloat(filterSaturation || '0'))}%)
      blur(${filterBlur || 0}px)
      sepia(${filterSepia || 0}%)
    ` : undefined,
  } : {};
  
  // Avatar with mask enabled and settings - render only for biolink page (not editor preview)
  if (maskEnabled && hasMaskSettings && widget.config.url && variant === 'biolinkPage') {
    const actualMaskSize = parseFloat(maskSize || '200');
    const actualCropX = parseFloat(cropX || '50');
    const actualCropY = parseFloat(cropY || '50');
    const actualCropScale = parseFloat(cropScale || '1');
    const actualCropRotation = parseFloat(cropRotation || '0');

    const actualMaskWidth = parseFloat(maskWidth || maskSize || '200');
    const actualMaskHeight = parseFloat(maskHeight || maskSize || '200');
    const actualBorderRadius = parseFloat(maskBorderRadius || '10');
    
    const maskStyle = {
      width: maskShape === 'circle' ? `${actualMaskSize}px` : `${actualMaskWidth}px`,
      height: maskShape === 'circle' ? `${actualMaskSize}px` : `${actualMaskHeight}px`,
      position: 'relative' as const,
      margin: '0 auto',
      display: 'block' as const,
      borderRadius: maskShape === 'circle' ? '50%' : `${actualBorderRadius}px`,
    };

    // Use larger base size to prevent cropping when scaling down - matches enhanced-image-dialog logic
    const baseSize = Math.max(actualMaskWidth, actualMaskHeight) * 2;
    
    const imageStyleForMask = {
      position: 'absolute' as const,
      left: `${actualCropX}%`,
      top: `${actualCropY}%`,
      width: `${baseSize}px`,
      height: `${baseSize}px`,
      transform: `translate(-50%, -50%) scale(${actualCropScale}) rotate(${actualCropRotation}deg)`,
      objectFit: 'contain' as const, // Use contain to show full image without cropping
      filter: hasFilterSettings ? `
        brightness(${100 + (parseFloat(filterBrightness || '0'))}%)
        contrast(${100 + (parseFloat(filterContrast || '0'))}%)
        saturate(${100 + (parseFloat(filterSaturation || '0'))}%)
        blur(${filterBlur || 0}px)
        sepia(${filterSepia || 0}%)
      ` : undefined,
    };

    const avatarImage = (
      <div
        className="overflow-hidden border-2 border-gray-200"
        style={maskStyle}
      >
        <img
          src={widget.config.url}
          alt=""
          className="absolute object-cover"
          style={imageStyleForMask}
        />
      </div>
    );

    if (widget.config.destinationUrl) {
      return <a href={widget.config.destinationUrl}>{avatarImage}</a>;
    }
    return avatarImage;
  }

  // Full image rendering when mask is disabled and for biolink page
  if (!maskEnabled && widget.config.url && variant === 'biolinkPage') {
    const actualWidth = parseFloat(fullImageWidth || '400');
    const actualMaxWidth = parseFloat(fullImageMaxWidth || '80');
    
    const fullImageStyle = {
      width: `${actualWidth}px`,
      maxWidth: `${actualMaxWidth}%`,
      height: 'auto',
      objectFit: 'contain' as const, // Ensure full image is visible without cropping
      filter: hasFilterSettings ? `
        brightness(${100 + (parseFloat(filterBrightness || '0'))}%)
        contrast(${100 + (parseFloat(filterContrast || '0'))}%)
        saturate(${100 + (parseFloat(filterSaturation || '0'))}%)
        blur(${filterBlur || 0}px)
        sepia(${filterSepia || 0}%)
      ` : undefined,
    };

    const fullImage = (
      <img
        className="object-contain rounded block mx-auto"
        src={widget.config.url}
        alt=""
        style={fullImageStyle}
      />
    );

    if (widget.config.destinationUrl) {
      return <a href={widget.config.destinationUrl}>{fullImage}</a>;
    }
    return fullImage;
  }

  // Regular image rendering (for editor variant or when mask is enabled but settings missing)
  const image = widget.config.url ? (
    <img
      className={clsx(
        'object-cover',
        getImageClassName({widget, variant}),
        hasCustomDimensions && 'max-w-full'
      )}
      src={widget.config.url}
      alt=""
      style={imageStyle}
    />
  ) : (
    <div
      className={clsx(
        getImageClassName({widget, variant}),
        'flex items-center justify-center'
      )}
      style={imageStyle}
    >
      <ImageIcon
        size={variant === 'editor' ? 'sm' : 'lg'}
        className="text-muted"
      />
    </div>
  );

  if (widget.config.destinationUrl) {
    return <a href={widget.config.destinationUrl}>{image}</a>;
  }
  return image;
}

function getImageClassName({
  widget,
  variant,
}: WidgetRendererProps<ImageWidget>) {
  const type = widget.config.type;
  const size = widget.config.size || 'medium';
  const customWidth = widget.config.width;
  const customHeight = widget.config.height;
  const cropX = widget.config.cropX;
  const cropY = widget.config.cropY;
  const cropScale = widget.config.cropScale;
  const maskSize = widget.config.maskSize;
  const maskX = widget.config.maskX;
  const maskY = widget.config.maskY;
  
  // Check if custom dimensions are actually provided and not empty
  // AND only for biolink page variant (not for editor)
  const hasCustomDimensions = (variant !== 'editor') && 
                              ((customWidth?.trim() && customWidth.trim() !== '') || 
                               (customHeight?.trim() && customHeight.trim() !== ''));
  
  // Check if avatar has crop settings
  const hasCropSettings = widget.config.type === 'avatar' && 
                         cropX !== undefined && 
                         cropY !== undefined && 
                         cropScale !== undefined;

  // Check if avatar has mask settings
  const hasMaskSettings = widget.config.type === 'avatar' &&
                          maskSize !== undefined &&
                          maskX !== undefined &&
                          maskY !== undefined;
  
  // For editor variant, always use small size (ignore custom dimensions and crop)
  if (variant === 'editor') {
    return `w-20 h-20 ${type === 'avatar' ? 'rounded-full' : 'rounded'}`;
  }
  
  // Custom dimensions take priority - remove conflicting classes
  if (hasCustomDimensions) {
    const borderRadius = type === 'avatar' ? 'rounded-full' : 'rounded';
    return `${borderRadius} block mx-auto`;
  }
  
  // Avatar type with crop or mask settings - only for biolink page
  if (type === 'avatar') {
    if ((hasCropSettings || hasMaskSettings) && variant === 'biolinkPage') {
      const width = hasMaskSettings ? `w-[${maskSize}px]` : 'w-96';
      const height = hasMaskSettings ? `h-[${maskSize}px]` : 'h-96';
      return `${width} ${height} rounded-full mx-auto overflow-hidden`;
    }
    return 'w-96 h-96 rounded-full mx-auto';
  }
  
  // Size-based classes for biolink page
  const sizeClasses = {
    small: 'w-48 h-48 mx-auto',
    medium: 'w-80 h-80 mx-auto',
    large: 'w-96 h-96 mx-auto',
    full: 'w-full h-auto',
  };
  
  return `${sizeClasses[size]} rounded block`;
}
